/**
 * M7 S8: resonance-meter proofs (spec §2.4, break-it EX-4). A meter reading counts as a `watch` / `knowledge` proof
 * only for a **distinct Tethys operation** not proven in the last `case.json meter.windowDays` faction days; every
 * other reading is texture (it still shows the trend on the Logs screen). The case counts at most
 * `meter.maxInCase` meter proofs (`case.ts`). The meter proof is also the `knowledge` link's repeatable
 * corroboration (§2.5), so it never goes through `takeCorroboration`.
 *
 * Pure and event-driven (a `meterReading` story event); Career-in, Career-out.
 */
import type { Career } from '../career/types'
import type { CaseConfig } from './schemas'
import { storyOf, withStory } from './state'
import type { MeterProof } from './types'

/** The career's meter proofs, oldest first. */
export function meterProofsOf(career: Career): readonly MeterProof[] {
  return storyOf(career).meterProofs ?? []
}

/** True when a reading of `opId` on faction day `day` would count as a new proof. */
export function meterReadingCounts(career: Career, opId: string, day: number, cfg: CaseConfig): boolean {
  const window = cfg.meter.windowDays
  for (const p of meterProofsOf(career)) {
    if (p.opId === opId && day - p.day < window && day >= p.day) return false
  }
  return true
}

export interface MeterReadingResult {
  career: Career
  /** The reading became a proof (false = texture only). */
  proof: boolean
}

/** Records a meter reading of Tethys operation `opId` on faction day `day`: a proof when it counts, else texture. */
export function recordMeterReading(career: Career, opId: string, day: number, cfg: CaseConfig): MeterReadingResult {
  if (!meterReadingCounts(career, opId, day, cfg)) return { career, proof: false }
  const next = withStory(career, (s) => ({ ...s, meterProofs: [...(s.meterProofs ?? []), { opId, day }] }))
  return { career: next, proof: true }
}
